import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import FilterSidebar from "./components/FilterSidebar";
import TrekCard from "./components/TrekCard";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { trekApi } from "../../services/api";

// ── filter helpers ────────────────────────────────────────────────────────────

function matchesDifficulty(trek, value) {
  if (value === "all" || !value) return true;
  const d = trek.trekData?.difficulty ?? trek.difficulty;
  if (value.includes("-")) {
    const [min, max] = value.split("-").map(Number);
    return d >= min && d <= max;
  }
  return d === Number(value);
}

function matchesSeason(trek, value) {
  if (value === "all" || !value) return true;
  const raw = trek.trekData?.bestSeason ?? trek.bestSeason ?? "";
  const s = raw.toLowerCase();
  if (value === "spring") return s.includes("mar") || s.includes("apr") || s.includes("may");
  if (value === "autumn") return s.includes("sep") || s.includes("oct") || s.includes("nov");
  if (value === "winter") return s.includes("dec") || s.includes("jan") || s.includes("feb");
  return s.includes(value);
}

function matchesDuration(trek, value) {
  if (value === "all" || !value) return true;
  const days = trek.trekData?.duration ?? trek.duration ?? 0;
  if (value === "22+") return days >= 22;
  const [min, max] = value.split("-").map(Number);
  return days >= min && days <= max;
}

function matchesElevation(trek, value) {
  if (value === "all" || !value) return true;
  const raw = trek.trekData?.maxAltitude ?? trek.maxAltitude ?? "0";
  const elev = parseInt(String(raw).replace(/[^0-9]/g, ""), 10);
  if (value === "5000+") return elev >= 5000;
  return elev <= parseInt(value, 10);
}

const INITIAL_FILTERS = {
  difficulty: "all",
  season: "all",
  duration: "all",
  elevation: "all",
};

// ── stat card ─────────────────────────────────────────────────────────────────
const StatPill = ({ icon, label, value }) => (
  <div className="flex items-center gap-2.5 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-white/10">
    <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
      <Icon name={icon} size={15} className="text-accent" />
    </div>
    <div>
      <p className="text-white font-bold text-sm leading-none">{value}</p>
      <p className="text-white/60 text-xs mt-0.5">{label}</p>
    </div>
  </div>
);

// ── active filter chip ────────────────────────────────────────────────────────
const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full border border-primary/20">
    {label}
    <button
      onClick={onRemove}
      className="w-4 h-4 rounded-full hover:bg-primary/20 flex items-center justify-center transition-colors"
      aria-label="Remove filter"
    >
      <Icon name="X" size={10} />
    </button>
  </span>
);

// ── main component ────────────────────────────────────────────────────────────
const TrekDiscoveryHub = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [allTreks, setAllTreks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("default");

  const [filters, setFilters] = useState(() => ({
    ...INITIAL_FILTERS,
    difficulty: searchParams.get("difficulty") || "all",
    season: searchParams.get("season") || "all",
    duration: searchParams.get("duration") || "all",
  }));

  useEffect(() => {
    const fetchTreks = async () => {
      try {
        setLoading(true);
        const raw = await trekApi.getAllTreks();
        const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:5001";
        const treks = raw.map((trek) => ({
          ...trek,
          trekData: {
            ...trek.trekData,
            heroImage: trek.trekData?.heroImage?.startsWith("/")
              ? `${backendUrl}${trek.trekData.heroImage}`
              : trek.trekData?.heroImage,
          },
        }));
        setAllTreks(treks);
      } catch (err) {
        setError(err.message || "Failed to load treks");
      } finally {
        setLoading(false);
      }
    };
    fetchTreks();
  }, []);

  const filteredTreks = useMemo(() => {
    let list = allTreks.filter(
      (t) =>
        matchesDifficulty(t, filters.difficulty) &&
        matchesSeason(t, filters.season) &&
        matchesDuration(t, filters.duration) &&
        matchesElevation(t, filters.elevation)
    );

    if (sortBy === "duration-asc") list = [...list].sort((a, b) => (a.trekData?.duration ?? 0) - (b.trekData?.duration ?? 0));
    if (sortBy === "duration-desc") list = [...list].sort((a, b) => (b.trekData?.duration ?? 0) - (a.trekData?.duration ?? 0));
    if (sortBy === "difficulty-asc") list = [...list].sort((a, b) => (a.trekData?.difficulty ?? 0) - (b.trekData?.difficulty ?? 0));
    if (sortBy === "difficulty-desc") list = [...list].sort((a, b) => (b.trekData?.difficulty ?? 0) - (a.trekData?.difficulty ?? 0));

    return list;
  }, [allTreks, filters, sortBy]);

  const handleFilterChange = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const handleClearFilters = () => setFilters(INITIAL_FILTERS);
  const handleViewDetails = (trek) => navigate(`/trek-discovery-hub/${trek.id}`);

  const hasActiveFilters = Object.values(filters).some((v) => v !== "all");

  const activeFilterLabels = [
    filters.difficulty !== "all" && { key: "difficulty", label: `Difficulty: ${filters.difficulty}` },
    filters.season !== "all" && { key: "season", label: `Season: ${filters.season}` },
    filters.duration !== "all" && { key: "duration", label: `Duration: ${filters.duration} days` },
    filters.elevation !== "all" && { key: "elevation", label: `Elevation: ${filters.elevation}m` },
  ].filter(Boolean);

  // ── loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-28 flex justify-center items-center h-96">
          <LoadingSpinner size="lg" text="Loading treks..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-32 flex flex-col items-center gap-4 px-4">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
            <Icon name="WifiOff" size={28} className="text-red-400" />
          </div>
          <p className="text-foreground font-semibold">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-2 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#fafaf7]">
      <Header />

      {/* ── Hero banner ─────────────────────────────────────────────────────── */}
      <div className="relative pt-28 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&h=700&fit=crop"
          alt="Himalayan trekking landscape"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        <svg
          className="absolute bottom-0 left-0 right-0 w-full opacity-[0.15]"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,120 L0,80 L120,20 L240,70 L360,10 L480,60 L600,0 L720,50 L840,15 L960,55 L1080,5 L1200,45 L1320,25 L1440,60 L1440,120 Z"
            fill="#fafaf7"
          />
        </svg>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent/20 backdrop-blur-sm rounded-full text-accent text-xs font-semibold mb-5 border border-accent/20">
              <Icon name="Compass" size={13} />
              {allTreks.length} treks available
            </div>
            <h1 className="font-headline text-4xl md:text-6xl text-white mb-4 leading-[0.95]">
              Discover Your<br />
              <span className="text-accent">Perfect Trek</span>
            </h1>
            <p className="text-white/80 text-lg mb-10 max-w-xl leading-relaxed">
              From beginner-friendly valleys to expert high-pass expeditions —
              filter by difficulty, season, and duration to find your ideal Himalayan adventure.
            </p>

            <div className="flex flex-wrap gap-3">
              <StatPill icon="Mountain" label="Highest point" value="6,461m" />
              <StatPill icon="Clock" label="Shortest trek" value="7 days" />
              <StatPill icon="Star" label="Success rate" value="95%+" />
            </div>
          </div>
        </div>
      </div>

      {/* ── Main layout ─────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-7">

          {/* Sidebar — sticky */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <FilterSidebar
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                isOpen={isFilterOpen}
                onToggle={() => setIsFilterOpen((o) => !o)}
              />
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 min-w-0">

            {/* ── Toolbar ─────────────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-border/50 shadow-sm px-5 py-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-headline font-bold text-foreground text-lg">
                      {filteredTreks.length}
                      <span className="font-normal text-text-secondary text-sm ml-1.5">
                        of {allTreks.length} treks
                      </span>
                    </span>
                    {hasActiveFilters && (
                      <span className="text-xs text-text-secondary bg-muted/50 px-2 py-0.5 rounded-full">filtered</span>
                    )}
                  </div>

                  {hasActiveFilters && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {activeFilterLabels.map(({ key, label }) => (
                        <FilterChip
                          key={key}
                          label={label}
                          onRemove={() => handleFilterChange(key, "all")}
                        />
                      ))}
                      <button
                        onClick={handleClearFilters}
                        className="text-xs text-gray-400 hover:text-primary transition-colors underline underline-offset-2"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setIsFilterOpen(true)}
                    className="lg:hidden flex items-center gap-1.5 px-3 py-2 border border-border rounded-xl text-sm text-text-secondary hover:border-primary/40 transition-colors"
                  >
                    <Icon name="SlidersHorizontal" size={15} />
                    Filters
                    {hasActiveFilters && (
                      <span className="w-2 h-2 rounded-full bg-primary" />
                    )}
                  </button>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="text-sm border border-border rounded-xl px-3 py-2 text-text-secondary bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 cursor-pointer"
                  >
                    <option value="default">Sort: Default</option>
                    <option value="duration-asc">Duration: Short first</option>
                    <option value="duration-desc">Duration: Long first</option>
                    <option value="difficulty-asc">Difficulty: Easy first</option>
                    <option value="difficulty-desc">Difficulty: Hard first</option>
                  </select>

                  <div className="flex border border-border rounded-xl overflow-hidden">
                    {[
                      { mode: "grid", icon: "LayoutGrid" },
                      { mode: "list", icon: "List" },
                    ].map(({ mode, icon }) => (
                      <button
                        key={mode}
                        onClick={() => setViewMode(mode)}
                        className={`px-3 py-2 transition-colors ${
                          viewMode === mode
                            ? "bg-primary text-white"
                            : "text-gray-400 hover:bg-muted/50"
                        }`}
                        aria-label={`${mode} view`}
                      >
                        <Icon name={icon} size={16} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Trek results ─────────────────────────────────────────────── */}
            {filteredTreks.length > 0 ? (
              <div
                className={
                  viewMode === "grid"
                    ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5"
                    : "flex flex-col gap-4"
                }
              >
                {filteredTreks.map((trek) => (
                  <TrekCard
                    key={trek.id}
                    trek={trek}
                    onViewDetails={handleViewDetails}
                    viewMode={viewMode}
                  />
                ))}
              </div>
            ) : (
              /* ── Empty state ──────────────────────────────────────────────── */
              <div className="bg-white rounded-2xl border border-border/50 py-20 text-center">
                <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-5">
                  <Icon name="SearchX" size={32} className="text-gray-300" />
                </div>
                <h3 className="font-headline font-bold text-xl text-foreground mb-2">
                  No treks match your filters
                </h3>
                <p className="text-text-secondary mb-6 max-w-sm mx-auto text-sm">
                  Try adjusting or clearing your filters — there are {allTreks.length} treks waiting for you.
                </p>
                <button
                  onClick={handleClearFilters}
                  className="px-6 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors shadow-sm"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter sidebar */}
      <FilterSidebar
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        isOpen={isFilterOpen}
        onToggle={() => setIsFilterOpen((o) => !o)}
        mobileOnly
      />
    </div>
  );
};

export default TrekDiscoveryHub;
